import React, { useState, useEffect } from 'react';
import { getClients, getShifts, getSettings, saveInvoice, getInvoices, getNextInvoiceNumber, deleteInvoice } from '../utils/storageUtils';
import { generateInvoicePDF } from '../utils/pdfUtils';
import { format, addDays } from 'date-fns';

// Helper function to safely format dates
function safeFormatDate(date, formatString) {
  if (!date) {
    return 'No Date';
  }

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime()) || dateObj.getTime() === 0) {
      return 'Invalid Date';
    }
    return format(dateObj, formatString);
  } catch (error) {
    return 'Format Error';
  }
}

function Invoices() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [allInvoices, setAllInvoices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [showDeleteId, setShowDeleteId] = useState(null);

  useEffect(() => {
    loadClients();
    loadInvoices();
    setSettings(getSettings());
  }, []);

  const loadClients = () => {
    const clientsData = getClients();
    setClients(clientsData.filter(c => c.status === 'active'));
  };

  const loadInvoices = () => {
    const invoicesData = getInvoices();
    const sorted = invoicesData.sort((a, b) => new Date(b.date) - new Date(a.date));
    setAllInvoices(sorted);
  };

  const handleGeneratePreview = () => {
    if (!selectedClientId || !startDate || !endDate) {
      alert('Please select a client and date range');
      return;
    }

    const allShifts = getShifts();
    const client = clients.find(c => c.id === selectedClientId);
    
    const filteredShifts = allShifts.filter(shift => {
      const shiftDate = new Date(shift.date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      return shift.clientId === selectedClientId &&
             shiftDate >= start &&
             shiftDate <= end &&
             shift.status === 'completed';
    });

    if (filteredShifts.length === 0) {
      alert('No completed shifts found for this client in the selected date range');
      return;
    }

    // Sort shifts chronologically in descending order (newest first)
    const sortedShifts = filteredShifts.sort((a, b) => new Date(b.date) - new Date(a.date));

    const lineItems = [];

    sortedShifts.forEach(shift => {
      const description = `[${format(new Date(shift.date), 'dd/MM/yyyy')}] ${shift.serviceDescription}`;
      const transportNote = shift.kilometers > 0 ? `\nActivity Based Transport: ${shift.kilometers}km` : '';
      
      lineItems.push({
        itemCode: shift.ndisItemCode,
        description: description + transportNote,
        quantity: shift.hours,
        unitPrice: shift.hourlyRate,
        amount: shift.serviceTotal
      });
    });

    const totalKilometers = sortedShifts.reduce((sum, shift) => sum + (shift.kilometers || 0), 0);
    if (totalKilometers > 0) {
      lineItems.push({
        itemCode: '04_590_0125_6_1',
        description: 'Kilometres driven during invoice period | Activity Based Transport – Social, Economic and Community Participation Supports',
        quantity: totalKilometers,
        unitPrice: 1.00,
        amount: totalKilometers
      });
    }

    const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
    const invoiceNumber = getNextInvoiceNumber();

    // Create invoice date from most recent shift
    let invoiceDate;
    try {
      const shiftDate = sortedShifts[0].date;
      invoiceDate = shiftDate instanceof Date ? shiftDate : new Date(shiftDate);

      // Validate the date
      if (isNaN(invoiceDate.getTime())) {
        invoiceDate = new Date(); // Fallback to today
      }
    } catch (error) {
      invoiceDate = new Date(); // Fallback to today
    }

    const dueDate = addDays(invoiceDate, settings.defaultInvoiceDueDays || 14);

    const invoice = {
      invoiceNumber,
      date: invoiceDate,
      dueDate,
      client,
      lineItems,
      subtotal,
      total: subtotal,
      shifts: sortedShifts,
      status: 'approved',
      paid: false
    };

    setPreviewInvoice(invoice);
  };

  const handleSaveInvoice = () => {
    if (!previewInvoice) return;

    const invoiceToSave = {
      ...previewInvoice,
      approvedAt: new Date().toISOString()
    };

    saveInvoice(invoiceToSave);

    loadInvoices();
    setShowForm(false);
    setPreviewInvoice(null);
    setSelectedClientId('');
    setStartDate('');
    setEndDate('');
    alert(`Invoice ${previewInvoice.invoiceNumber} saved successfully!`);
  };

  const handleDownloadPDF = async (invoice = null) => {
    try {
      const invoiceToDownload = invoice || previewInvoice;
      if (!invoiceToDownload) {
        alert('No invoice data available to download');
        return;
      }

      if (!settings) {
        alert('Settings not loaded. Please check your settings configuration.');
        return;
      }

      const pdf = await generateInvoicePDF(invoiceToDownload, settings);
      const clientName = invoiceToDownload.client?.name || invoiceToDownload.clientName || 'Client';
      const filename = `${invoiceToDownload.invoiceNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleTogglePaid = (invoice) => {
    const updatedInvoice = {
      ...invoice,
      paid: !invoice.paid,
      paidAt: !invoice.paid ? new Date().toISOString() : null
    };
    saveInvoice(updatedInvoice);
    loadInvoices();
  };

  const handleViewInvoice = (invoice) => {
    setViewingInvoice(invoice);
  };

  const handleDeleteInvoice = (invoiceId) => {
    if (window.confirm('Are you sure you want to delete this invoice? This cannot be undone.')) {
      deleteInvoice(invoiceId);
      loadInvoices();
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setPreviewInvoice(null);
    setSelectedClientId('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="invoices-page">
      <div className="page-header">
        <h1>Invoices</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : '+ Add Invoice'}
        </button>
      </div>

      {showForm && (
        <div className="invoice-form-container" style={{ marginBottom: '2rem' }}>
          {!previewInvoice ? (
            <>
              <h2>Generate New Invoice</h2>
              <div className="invoice-form">
                <div className="form-group">
                  <label>Select Client</label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                  >
                    <option value="">Choose a client...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name} ({client.ndisNumber})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <button 
                  className="btn btn-primary" 
                  onClick={handleGeneratePreview}
                >
                  Preview Invoice
                </button>
              </div>
            </>
          ) : (
            <div className="invoice-preview">
              <div className="preview-header">
                <h2>Invoice Preview</h2>
                <div className="preview-actions">
                  <button 
                    className="btn btn-success" 
                    onClick={handleSaveInvoice}
                    style={{ marginRight: '1rem' }}
                  >
                    Save Invoice
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => handleDownloadPDF()}
                    style={{ marginRight: '1rem' }}
                  >
                    Download PDF
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    onClick={handleCancelForm}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              <div className="invoice-document">
                <div className="invoice-header-section">
                  <div className="business-info">
                    <h3>{settings.businessName}</h3>
                    <p>{settings.address}</p>
                    <p>{settings.suburb} {settings.state} {settings.postcode}</p>
                    <p>ABN: {settings.abn}</p>
                  </div>
                </div>

                <h1>TAX INVOICE</h1>

                <div className="invoice-details-section">
                  <div className="client-info">
                    {previewInvoice.client.fundingType === 'plan-managed' ? (
                      <>
                        <p><strong>Attention To: {previewInvoice.client.planManagerName}</strong></p>
                        {previewInvoice.client.planManagerOrganization && (
                          <p>{previewInvoice.client.planManagerOrganization}</p>
                        )}
                        {previewInvoice.client.planManagerEmail && (
                          <p>{previewInvoice.client.planManagerEmail}</p>
                        )}
                        <p style={{ marginTop: '1rem' }}><strong>Regarding Client: {previewInvoice.client.name}</strong></p>
                      </>
                    ) : (
                      <>
                        <p><strong>Attention To: {previewInvoice.client.name}</strong></p>
                      </>
                    )}
                    <p>NDIS: {previewInvoice.client.ndisNumber}</p>
                    {previewInvoice.client.dob && (
                      <p>DOB: {safeFormatDate(previewInvoice.client.dob, 'dd/MM/yyyy')}</p>
                    )}
                    {previewInvoice.client.jobDescription && (
                      <p>Job Description: {previewInvoice.client.jobDescription}</p>
                    )}
                  </div>

                  <div className="invoice-meta">
                    <p>Invoice Date: {safeFormatDate(previewInvoice.date, 'dd MMM yyyy')}</p>
                    <p>Account Number: {previewInvoice.client.ndisNumber}</p>
                    <p>Invoice Number: {previewInvoice.invoiceNumber}</p>
                    <p>ABN: {settings.abn}</p>
                  </div>
                </div>

                <table className="invoice-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Amount AUD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewInvoice.lineItems.map((item, index) => {
                      // Extract date from description if it exists (format: [DD/MM/YYYY])
                      const dateMatch = item.description.match(/\[(\d{2}\/\d{2}\/\d{4})\]/);
                      const dateStr = dateMatch ? dateMatch[1] : '';
                      let cleanDescription = dateMatch
                        ? item.description.replace(/\[\d{2}\/\d{2}\/\d{4}\]\s*/, '')
                        : item.description;

                      // Format transport text to appear on new line (replace \n with <br> for HTML)
                      const formattedDescription = cleanDescription.split('\n').map((line, i) => (
                        i === 0 ? line : <><br key={i}/>{line}</>
                      ));

                      return (
                        <tr key={index}>
                          <td data-label="Item">{item.itemCode}</td>
                          <td data-label="Date">{dateStr}</td>
                          <td data-label="Description">{formattedDescription}</td>
                          <td data-label="Quantity">{item.quantity.toFixed(2)}</td>
                          <td data-label="Unit Price">${item.unitPrice.toFixed(2)}</td>
                          <td data-label="Amount">${item.amount.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="5" className="text-right"><strong>Subtotal</strong></td>
                      <td><strong>${previewInvoice.subtotal.toFixed(2)}</strong></td>
                    </tr>
                    <tr>
                      <td colSpan="5" className="text-right"><strong>TOTAL AUD</strong></td>
                      <td><strong>${previewInvoice.total.toFixed(2)}</strong></td>
                    </tr>
                  </tfoot>
                </table>

                <div className="invoice-footer">
                  <p><strong>Due Date: {safeFormatDate(previewInvoice.dueDate, 'dd MMM yyyy')}</strong></p>
                  <p>Please make payment to:</p>
                  <p>Account Name: {settings.bankName}</p>
                  <p>BSB: {settings.bsb}</p>
                  <p>Account Number: {settings.accountNumber}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="shifts-table">
        <table>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Client</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Paid</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {allInvoices.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                  No invoices yet. Click "+ Add Invoice" to create your first invoice.
                </td>
              </tr>
            ) : (
              allInvoices.map(invoice => (
                <tr key={invoice.id}>
                  <td data-label="Invoice #"><strong>{invoice.invoiceNumber}</strong></td>
                  <td data-label="Client">{invoice.client?.name || invoice.clientName || 'Unknown'}</td>
                  <td data-label="Date">{safeFormatDate(invoice.date || invoice.createdAt, 'dd/MM/yyyy')}</td>
                  <td data-label="Amount" style={{ color: '#2ecc71', fontWeight: 'bold' }}>
                    ${(parseFloat(invoice.total) || 0).toFixed(2)}
                  </td>
                  <td data-label="Status">
                    <button
                      className={`status-badge ${invoice.paid ? 'paid' : 'unpaid'}`}
                      onClick={() => handleTogglePaid(invoice)}
                      style={{ cursor: 'pointer', border: 'none' }}
                      title={`Click to mark as ${invoice.paid ? 'unpaid' : 'paid'}`}
                    >
                      {invoice.paid ? '✓ Paid' : 'Unpaid'}
                    </button>
                  </td>
                  <td data-label="Actions">
                    <div className="action-buttons">
                      <button 
                        className="btn btn-small btn-success"
                        onClick={() => handleViewInvoice(invoice)}
                      >
                        View
                      </button>
                      <button 
                        className="btn btn-small btn-info"
                        onClick={() => handleDownloadPDF(invoice)}
                      >
                        Download
                      </button>
                      <div className="invoice-dots-wrapper" style={{ position: 'relative' }}>
                        <button 
                          className="btn-dots"
                          onClick={() => setShowDeleteId(showDeleteId === invoice.id ? null : invoice.id)}
                          title="More options"
                        >
                          ⋯
                        </button>
                        {showDeleteId === invoice.id && (
                          <button
                            className="btn btn-small btn-danger"
                            style={{ position: 'absolute', left: '0', top: '100%', marginTop: '4px', zIndex: 5, whiteSpace: 'nowrap' }}
                            onClick={() => { handleDeleteInvoice(invoice.id); setShowDeleteId(null); }}
                          >
                            Delete Invoice
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {viewingInvoice && (
        <div className="modal-overlay" onClick={() => setViewingInvoice(null)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', padding: '0', position: 'relative' }}>
            {/* Close button in top right */}
            <button 
              className="btn-close" 
              onClick={() => setViewingInvoice(null)}
              style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', zIndex: 10 }}
            >
              ×
            </button>
            
            <div className="modal-body" style={{ padding: '0' }}>
              {/* Invoice content in gray box */}
              <div className="client-card" style={{ 
                margin: '3rem auto 2rem auto',
                padding: '2.5rem',
                maxWidth: '900px'
              }}>
                <div className="invoice-document">
                <div className="invoice-header-section">
                  <div className="business-info">
                    <h3>{settings.businessName}</h3>
                    <p>{settings.address}</p>
                    <p>{settings.suburb} {settings.state} {settings.postcode}</p>
                    <p>ABN: {settings.abn}</p>
                  </div>
                </div>

                <h1>TAX INVOICE</h1>

                <div className="invoice-details-section">
                  <div className="client-info">
                    {viewingInvoice.client.fundingType === 'plan-managed' ? (
                      <>
                        <p><strong>Attention To: {viewingInvoice.client.planManagerName}</strong></p>
                        {viewingInvoice.client.planManagerOrganization && (
                          <p>{viewingInvoice.client.planManagerOrganization}</p>
                        )}
                        {viewingInvoice.client.planManagerEmail && (
                          <p>{viewingInvoice.client.planManagerEmail}</p>
                        )}
                        <p style={{ marginTop: '1rem' }}><strong>Regarding Client: {viewingInvoice.client.name}</strong></p>
                      </>
                    ) : (
                      <>
                        <p><strong>Attention To: {viewingInvoice.client.name}</strong></p>
                      </>
                    )}
                    <p>NDIS: {viewingInvoice.client.ndisNumber}</p>
                    {viewingInvoice.client.dob && (
                      <p>DOB: {safeFormatDate(viewingInvoice.client.dob, 'dd/MM/yyyy')}</p>
                    )}
                    {viewingInvoice.client.jobDescription && (
                      <p>Job Description: {viewingInvoice.client.jobDescription}</p>
                    )}
                  </div>

                  <div className="invoice-meta">
                    <p>Invoice Date: {safeFormatDate(viewingInvoice.date, 'dd MMM yyyy')}</p>
                    <p>Account Number: {viewingInvoice.client.ndisNumber}</p>
                    <p>Invoice Number: {viewingInvoice.invoiceNumber}</p>
                    <p>ABN: {settings.abn}</p>
                  </div>
                </div>

                <table className="invoice-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Amount AUD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingInvoice.lineItems.map((item, index) => {
                      // Extract date from description if it exists (format: [DD/MM/YYYY])
                      const dateMatch = item.description.match(/\[(\d{2}\/\d{2}\/\d{4})\]/);
                      const dateStr = dateMatch ? dateMatch[1] : '';
                      let cleanDescription = dateMatch
                        ? item.description.replace(/\[\d{2}\/\d{2}\/\d{4}\]\s*/, '')
                        : item.description;

                      // Format transport text to appear on new line (replace \n with <br> for HTML)
                      const formattedDescription = cleanDescription.split('\n').map((line, i) => (
                        i === 0 ? line : <><br key={i}/>{line}</>
                      ));

                      return (
                        <tr key={index}>
                          <td data-label="Item">{item.itemCode}</td>
                          <td data-label="Date">{dateStr}</td>
                          <td data-label="Description">{formattedDescription}</td>
                          <td data-label="Quantity">{item.quantity.toFixed(2)}</td>
                          <td data-label="Unit Price">${item.unitPrice.toFixed(2)}</td>
                          <td data-label="Amount">${item.amount.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="5" className="text-right"><strong>Subtotal</strong></td>
                      <td><strong>${viewingInvoice.subtotal.toFixed(2)}</strong></td>
                    </tr>
                    <tr>
                      <td colSpan="5" className="text-right"><strong>TOTAL AUD</strong></td>
                      <td><strong>${viewingInvoice.total.toFixed(2)}</strong></td>
                    </tr>
                  </tfoot>
                </table>

                <div className="invoice-footer">
                  <p><strong>Due Date: {safeFormatDate(viewingInvoice.dueDate, 'dd MMM yyyy')}</strong></p>
                  <p>Please make payment to:</p>
                  <p>Account Name: {settings.bankName}</p>
                  <p>BSB: {settings.bsb}</p>
                  <p>Account Number: {settings.accountNumber}</p>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Invoices;
